import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Field, Form, Formik } from "formik";
import { Check, Paperclip, Pencil, X } from "lucide-react";
import { z } from "zod";

import { RichEditor } from "components/RichEditor/RichEditor";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { useSenderEmails } from "features/comunications/hooks/useSenderEmails";
import { useSendEmail } from "features/patients/hooks/useSendEmail";

import { useEmailTemplates } from "../../hooks/useEmailTemplates";
import styles from "./SendEmailModal.module.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEditorEmpty = (value) =>
  !value || value.replace(/<[^>]*>/g, "").trim().length === 0;

const emailSchema = z.object({
  subject: z.string().min(1, "Subject is required"),
  body: z.string().refine((val) => !isEditorEmpty(val), "Message is required"),
  templateId: z.string().optional(),
});

const validateWithZod = (schema) => (values) => {
  try {
    schema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.reduce((acc, err) => {
        const path = err.path.join(".");
        acc[path] = err.message;
        return acc;
      }, {});
    }
    return {};
  }
};

export const SendEmailModal = ({
  isOpen,
  onClose,
  patient,
  documents = [],
  documentIds = "",
  // Optional override called instead of the generic /sendemail mutation.
  // Receives ({ patient, subject, body, senderEmail }) and must return a
  // promise. Used by domain-specific email endpoints (e.g. sick certs)
  // where the backend renders/attaches its own PDF and the generic
  // documentIds CSV doesn't apply.
  onSendOverride,
}) => {
  const uniquePatients = useMemo(() => {
    const patientsMap = new Map();

    if (patient?.id) {
      patientsMap.set(String(patient.id), {
        id: patient.id,
        firstName: patient.firstName,
        surname: patient.surname,
        email: patient.email,
        documents: [...documents],
      });
    }

    documents.filter(Boolean).forEach((doc) => {
      const docPatient = doc.patient || doc.patientDetails;
      const patientId = docPatient?.id || doc.patientId;

      if (patientId && !patientsMap.has(String(patientId))) {
        patientsMap.set(String(patientId), {
          id: patientId,
          firstName:
            docPatient?.firstName || docPatient?.name?.split(" ")[0] || "",
          surname:
            docPatient?.surname ||
            docPatient?.lastName ||
            docPatient?.name?.split(" ").slice(1).join(" ") ||
            "",
          email: docPatient?.email || docPatient?.emailAddress || "",
          documents: [],
        });
      }

      if (patientId) {
        const existing = patientsMap.get(String(patientId));
        if (existing && !existing.documents.find((d) => d.id === doc.id)) {
          existing.documents.push(doc);
        }
      }
    });

    return Array.from(patientsMap.values());
  }, [patient, documents]);

  const [selectedPatientIds, setSelectedPatientIds] = useState([]);

  // Per-patient recipient-email overrides — keyed by patient id (string).
  // The user can click the pencil icon to edit the email address before
  // sending. If the patient has no email on file we auto-open edit mode
  // so the user is forced to enter one before the Send button enables.
  const [recipientEmails, setRecipientEmails] = useState({});
  const [editingEmailIds, setEditingEmailIds] = useState(new Set());

  useEffect(() => {
    setSelectedPatientIds(uniquePatients.map((p) => String(p.id)));
    // Initialise the overrides + auto-open edit on any patient without
    // an email. We only seed entries we don't already have so an
    // in-progress edit isn't clobbered when the patient list updates.
    setRecipientEmails((prev) => {
      const next = { ...prev };
      uniquePatients.forEach((p) => {
        const idKey = String(p.id);
        if (!(idKey in next)) next[idKey] = p.email || "";
      });
      return next;
    });
    setEditingEmailIds((prev) => {
      const next = new Set(prev);
      uniquePatients.forEach((p) => {
        if (!p.email) next.add(String(p.id));
      });
      return next;
    });
  }, [uniquePatients]);

  const getRecipientEmail = (p) => {
    if (!p) return "";
    const idKey = String(p.id);
    return recipientEmails[idKey] ?? p.email ?? "";
  };

  const isEditingEmail = (p) => editingEmailIds.has(String(p?.id));

  const setRecipientEmail = (patientId, value) => {
    setRecipientEmails((prev) => ({ ...prev, [String(patientId)]: value }));
  };

  const toggleEditEmail = (patientId, force) => {
    const idKey = String(patientId);
    setEditingEmailIds((prev) => {
      const next = new Set(prev);
      const shouldEdit = force ?? !next.has(idKey);
      if (shouldEdit) next.add(idKey);
      else next.delete(idKey);
      return next;
    });
  };

  const hasMultiplePatients = uniquePatients.length > 1;
  const hasNoPatients = uniquePatients.length === 0;
  const singlePatient =
    uniquePatients.length === 1
      ? uniquePatients[0]
      : uniquePatients.length > 0
      ? uniquePatients[0]
      : patient;

  const isValidEmail = (value) =>
    typeof value === "string" && EMAIL_REGEX.test(value.trim());

  const selectedPatientsWithoutEmail = useMemo(() => {
    if (hasMultiplePatients) {
      return uniquePatients
        .filter((p) => selectedPatientIds.includes(String(p.id)))
        .filter((p) => !isValidEmail(getRecipientEmail(p)));
    }
    return singlePatient && !isValidEmail(getRecipientEmail(singlePatient))
      ? [singlePatient]
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMultiplePatients,
    uniquePatients,
    selectedPatientIds,
    singlePatient,
    recipientEmails,
  ]);

  const hasEmailError = selectedPatientsWithoutEmail.length > 0;

  const { data: emailTemplates = [], isLoading: isLoadingTemplates } =
    useEmailTemplates(patient?.id);
  const { data: senderEmails = [], isLoading: isLoadingSenderEmails } =
    useSenderEmails();
  const sendEmailMutation = useSendEmail();

  const [senderEmail, setSenderEmail] = useState("");

  useEffect(() => {
    if (senderEmails.length > 0 && !senderEmail) {
      setSenderEmail(senderEmails[0]);
    }
  }, [senderEmails, senderEmail]);

  const handlePatientToggle = (patientId) => {
    const idStr = String(patientId);
    setSelectedPatientIds((prev) => {
      if (prev.includes(idStr)) {
        return prev.filter((id) => id !== idStr);
      }
      return [...prev, idStr];
    });
  };

  const handleSelectAll = () => {
    if (selectedPatientIds.length === uniquePatients.length) {
      setSelectedPatientIds([]);
    } else {
      setSelectedPatientIds(uniquePatients.map((p) => String(p.id)));
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    const patientsToSend = hasMultiplePatients
      ? uniquePatients.filter((p) => selectedPatientIds.includes(String(p.id)))
      : [singlePatient];

    if (patientsToSend.length === 0 || hasNoPatients) {
      toast.error("Please select at least one patient");
      return;
    }

    const patientsWithoutEmail = patientsToSend.filter(
      (p) => !isValidEmail(getRecipientEmail(p))
    );
    if (patientsWithoutEmail.length > 0) {
      toast.error("Please enter a valid email address for every recipient");
      patientsWithoutEmail.forEach((p) => toggleEditEmail(p.id, true));
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const targetPatient of patientsToSend) {
      const patientDocs = hasMultiplePatients
        ? documents.filter((d) => {
            const docPatientId = d.patient?.id || d.patientId;
            return String(docPatientId) === String(targetPatient.id);
          })
        : documents;

      const docIds = patientDocs.map((d) => d.id).join(",") || documentIds;

      // Apply the (possibly edited) recipient email so both the
      // override and the generic /sendemail path send to whatever the
      // user just typed in the modal — not the stale patient.email.
      const resolvedEmail = getRecipientEmail(targetPatient).trim();
      const patientWithResolvedEmail = {
        ...targetPatient,
        email: resolvedEmail,
      };

      try {
        if (onSendOverride) {
          // Caller is in charge of the actual send (e.g. the sick cert
          // endpoint renders its own PDF) — we just forward the form
          // values + the chosen patient (with the resolved email).
          await onSendOverride({
            patient: patientWithResolvedEmail,
            subject: values.subject,
            body: values.body,
            senderEmail,
          });
        } else {
          const emailData = {
            emailAddress: resolvedEmail,
            patientId: targetPatient.id,
            subject: values.subject,
            body: values.body,
            documentIds: docIds,
            senderEmailAddress: senderEmail,
          };
          await sendEmailMutation.mutateAsync(emailData);
        }
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(
          `Failed to send email to ${targetPatient.firstName}:`,
          error
        );
      }
    }

    if (successCount > 0) {
      toast.success(
        patientsToSend.length === 1
          ? "Email sent successfully"
          : `Email sent to ${successCount} patient(s)`
      );
      resetForm();
      handleClose();
    }
    if (errorCount > 0) {
      toast.error(`Failed to send email to ${errorCount} patient(s)`);
    }
  };

  const handleClose = () => {
    setSelectedPatientIds(uniquePatients.map((p) => p.id));
    onClose();
  };

  const formatDocumentType = (type) => {
    if (!type) return "Document";
    return type
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getPatientFullName = (p) => {
    return `${p?.firstName || ""} ${p?.surname || ""}`.trim() || "Unknown";
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Send Email</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
          >
            <X size={24} />
          </button>
        </div>

        <Formik
          initialValues={{
            templateId: "",
            subject: "",
            body: "",
          }}
          validate={validateWithZod(emailSchema)}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form>
              <div className={styles.content}>
                {hasNoPatients ? (
                  <div className={styles.patientInfo}>
                    <div className={styles.patientInfoRow}>
                      <span
                        className={styles.patientInfoValue}
                        style={{ color: "#dc2626" }}
                      >
                        No patient information found in selected documents.
                      </span>
                    </div>
                  </div>
                ) : hasMultiplePatients ? (
                  <div className={styles.formGroup}>
                    <div className={styles.labelRow}>
                      <label className={styles.label}>
                        Recipients ({selectedPatientIds.length} of{" "}
                        {uniquePatients.length} selected)
                      </label>
                      <button
                        type="button"
                        className={styles.selectAllButton}
                        onClick={handleSelectAll}
                      >
                        {selectedPatientIds.length === uniquePatients.length
                          ? "Deselect all"
                          : "Select all"}
                      </button>
                    </div>
                    <div className={styles.patientsList}>
                      {uniquePatients.map((p) => {
                        const rowEmail = getRecipientEmail(p);
                        const rowValid = isValidEmail(rowEmail);
                        const editing = isEditingEmail(p);
                        return (
                          <div
                            key={p.id}
                            className={`${styles.patientItem} ${
                              selectedPatientIds.includes(String(p.id))
                                ? styles.patientItemSelected
                                : ""
                            } ${!rowValid ? styles.patientItemError : ""}`}
                            onClick={(e) => {
                              // Don't toggle selection when interacting
                              // with the email edit controls.
                              if (
                                e.target.closest("input") ||
                                e.target.closest("button")
                              ) {
                                return;
                              }
                              handlePatientToggle(p.id);
                            }}
                          >
                            <CustomCheckbox
                              checked={selectedPatientIds.includes(String(p.id))}
                              onChange={() => handlePatientToggle(p.id)}
                            />
                            <div className={styles.patientItemInfo}>
                              <span className={styles.patientItemName}>
                                {getPatientFullName(p)}
                              </span>
                              {editing ? (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <input
                                    type="email"
                                    className={styles.input}
                                    style={{
                                      height: "28px",
                                      padding: "0 8px",
                                      fontSize: "13px",
                                    }}
                                    value={rowEmail}
                                    placeholder="Enter recipient email"
                                    onChange={(e) =>
                                      setRecipientEmail(p.id, e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <button
                                    type="button"
                                    aria-label="Save email"
                                    className={styles.iconButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleEditEmail(p.id, false);
                                    }}
                                  >
                                    <Check size={14} />
                                  </button>
                                </span>
                              ) : (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <span
                                    className={`${styles.patientItemEmail} ${
                                      !rowValid ? styles.emailError : ""
                                    }`}
                                  >
                                    {rowEmail || "No email address"}
                                  </span>
                                  <button
                                    type="button"
                                    aria-label="Edit recipient email"
                                    className={styles.iconButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleEditEmail(p.id, true);
                                    }}
                                  >
                                    <Pencil size={12} />
                                  </button>
                                </span>
                              )}
                            </div>
                            <span className={styles.patientItemDocs}>
                              {p.documents?.length || 0} doc(s)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {hasEmailError && selectedPatientIds.length > 0 && (
                      <span className={styles.errorText}>
                        {selectedPatientsWithoutEmail.length === 1
                          ? `${getPatientFullName(
                              selectedPatientsWithoutEmail[0]
                            )} has no email address`
                          : `${selectedPatientsWithoutEmail.length} selected patients have no email address`}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={styles.patientInfo}>
                    <div className={styles.patientInfoRow}>
                      <span className={styles.patientInfoLabel}>Patient:</span>
                      <span className={styles.patientInfoValue}>
                        {getPatientFullName(singlePatient)}
                      </span>
                    </div>
                    <div className={styles.patientInfoRow}>
                      <span className={styles.patientInfoLabel}>Email:</span>
                      {isEditingEmail(singlePatient) ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            flex: 1,
                          }}
                        >
                          <input
                            type="email"
                            className={styles.input}
                            style={{ flex: 1, minWidth: 0 }}
                            value={getRecipientEmail(singlePatient)}
                            placeholder="Enter recipient email"
                            onChange={(e) =>
                              setRecipientEmail(singlePatient?.id, e.target.value)
                            }
                            autoFocus
                          />
                          <button
                            type="button"
                            aria-label="Save email"
                            className={styles.iconButton}
                            onClick={() =>
                              toggleEditEmail(singlePatient?.id, false)
                            }
                          >
                            <Check size={16} />
                          </button>
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span
                            className={`${styles.patientInfoValue} ${
                              !isValidEmail(getRecipientEmail(singlePatient))
                                ? styles.emailErrorText
                                : ""
                            }`}
                          >
                            {getRecipientEmail(singlePatient) ||
                              "No email address"}
                          </span>
                          <button
                            type="button"
                            aria-label="Edit recipient email"
                            className={styles.iconButton}
                            onClick={() =>
                              toggleEditEmail(singlePatient?.id, true)
                            }
                          >
                            <Pencil size={14} />
                          </button>
                        </span>
                      )}
                    </div>
                    {!isValidEmail(getRecipientEmail(singlePatient)) && (
                      <span className={styles.errorText}>
                        Please enter a valid email address before sending.
                      </span>
                    )}
                  </div>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="templateId">
                      Email Template (Optional)
                    </label>
                    <Field
                      as="select"
                      id="templateId"
                      name="templateId"
                      className={styles.select}
                      disabled={isLoadingTemplates}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        setFieldValue("templateId", templateId);

                        if (templateId) {
                          const template = emailTemplates.find(
                            (t) => t.id === parseInt(templateId)
                          );
                          if (template) {
                            setFieldValue("subject", template.subject || "");
                            setFieldValue("body", template.template || "");
                          }
                        }
                      }}
                    >
                      <option value="">
                        {isLoadingTemplates
                          ? "Loading templates..."
                          : "Select a template"}
                      </option>
                      {emailTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </Field>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="subject">
                      Subject
                    </label>
                    <Field
                      type="text"
                      id="subject"
                      name="subject"
                      className={`${styles.input} ${
                        errors.subject && touched.subject
                          ? styles.inputError
                          : ""
                      }`}
                      placeholder="Enter email subject"
                    />
                    {errors.subject && touched.subject && (
                      <span className={styles.errorText}>{errors.subject}</span>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label} htmlFor="senderEmail">
                    From (Sender Email)
                  </label>
                  <select
                    id="senderEmail"
                    className={styles.select}
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    disabled={isLoadingSenderEmails || senderEmails.length <= 1}
                  >
                    {isLoadingSenderEmails ? (
                      <option value="">Loading sender emails...</option>
                    ) : senderEmails.length === 0 ? (
                      <option value="">No sender emails configured</option>
                    ) : (
                      senderEmails.map((email) => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Message</label>
                  <div className={styles.richEditorWrapper}>
                    <RichEditor
                      editorValue={values.body}
                      onChange={(value) => setFieldValue("body", value)}
                    />
                  </div>
                  {errors.body && touched.body && (
                    <span className={styles.errorText}>{errors.body}</span>
                  )}
                </div>

                {documents.length > 0 && (
                  <div className={styles.attachmentsSection}>
                    <label className={styles.label}>
                      Attachments ({documents.length})
                    </label>
                    <div className={styles.attachmentsList}>
                      {documents.map((doc, index) => (
                        <div
                          key={doc.id || index}
                          className={styles.attachmentItem}
                        >
                          <Paperclip
                            size={18}
                            className={styles.attachmentIcon}
                          />
                          <div className={styles.attachmentInfo}>
                            <div className={styles.attachmentName}>
                              {doc.name || "Untitled Document"}
                            </div>
                            <div className={styles.attachmentType}>
                              {formatDocumentType(doc.documentType)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleClose}
                  disabled={sendEmailMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.sendButton}
                  disabled={
                    sendEmailMutation.isPending ||
                    hasNoPatients ||
                    (hasMultiplePatients && selectedPatientIds.length === 0) ||
                    hasEmailError
                  }
                >
                  {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
